/**
 * The Revit Family Builder (#255) — Revit families authored with the agent: a
 * list of the families saved for the selected Revit's version, and one family's
 * editor (details and parameters, its conversation, its versions). Ported from
 * `bimgo-ai-web`'s `FamilyAuthoringPage`; the feature itself lives in
 * `family-builder/`.
 *
 * It drives one Revit — the one picked in the title bar's instance picker — so
 * before anything else the pane says what is missing, in the order it would be
 * met: the local agent, the sign-in, then a Revit. An AutoCAD pick is not a
 * Revit, and the pane says that rather than listing nothing. Switching Revit
 * closes the open family: families are stored per Revit version, and the one on
 * screen belongs to the instance it was opened against.
 */
import { useEffect, useMemo, useState, type ComponentProps } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import type { AgentTarget } from "../lib/agentClient";
import type { Selection } from "../lib/selectedInstance";
import { BoxIcon } from "../components/skills/icons";
import { createFamilyBuilderClient } from "../family-builder/api";
import { FamilyBuilderEditor } from "../family-builder/FamilyBuilderEditor";
import { FamilyBuilderList } from "../family-builder/FamilyBuilderList";
import "../family-builder/family-builder.css";

export function FamilyBuilderHost({ visible, ...props }: ComponentProps<typeof FamilyBuilderPane> & { visible: boolean }) {
  const [visited, setVisited] = useState(visible);
  useEffect(() => { if (visible) setVisited(true); }, [visible]);
  return visible || visited ? (
    <div className="app-pane is-wide" hidden={!visible} style={visible ? undefined : { display: "none" }}>
      <FamilyBuilderPane key={JSON.stringify([props.orgId, props.userId, Boolean(props.token), props.revit.instance?.sessionId])} {...props} />
    </div>
  ) : null;
}

export default function FamilyBuilderPane({
  agent,
  token,
  orgId,
  userId,
  projectId,
  revit,
  otherVendorSelected,
  onBusyChange,
}: {
  agent: AgentTarget;
  token: string | null;
  orgId: string | null;
  userId: string | null;
  projectId: string | null;
  /** The title bar's Revit pick. */
  revit: Selection;
  /** Something is picked, but it is not a Revit (an AutoCAD). */
  otherVendorSelected: boolean;
  onBusyChange: (busy: boolean) => void;
}) {
  const { t } = useLocale();
  const sessionId = revit.instance?.host_kind === "revit" ? revit.instance.sessionId : null;
  const revitVersion = revit.instance?.revitVersion ?? null;
  const [openId, setOpenId] = useState<string | null>(null);
  useEffect(() => { if (!openId) onBusyChange(false); }, [openId, onBusyChange]);
  // One client per instance and sign-in: the list refetches when it changes, not on every render.
  const client = useMemo(
    () => (token && orgId && sessionId ? createFamilyBuilderClient({ token, orgId, targetSessionId: sessionId }) : null),
    [token, orgId, sessionId],
  );

  // A family belongs to the instance it was opened against.
  useEffect(() => {
    setOpenId(null);
  }, [sessionId]);

  const head = (actions?: React.ReactNode, version?: string | null) => (
    <div className="pane-head">
      <div className="pane-head-title">
        <h2>{t("familyBuilder.title")}</h2>
        {version ?? revitVersion ? <span className="family-builder-version">Revit {version ?? revitVersion}</span> : null}
      </div>
      {actions ? <div className="pane-head-actions">{actions}</div> : null}
    </div>
  );

  const empty = (title: string, lines: string[]) => (
    <>
      {head()}
      <div className="settings-card skills-empty">
        <span className="skills-empty-glyph">
          <BoxIcon size={22} />
        </span>
        <p className="skills-empty-title">{title}</p>
        {lines.map((line) => (
          <p key={line} className="skills-empty-desc">
            {line}
          </p>
        ))}
      </div>
    </>
  );

  let body: React.ReactNode;
  if (!agent.ok) {
    // The shell owns connection errors; keep the feature's empty state here.
    body = empty(t("familyBuilder.emptyTitle"), [t("familyBuilder.emptyDescription")]);
  } else if (!token || !orgId) {
    body = empty(t("familyBuilder.missingSession"), []);
  } else if (!sessionId || !client) {
    body = otherVendorSelected
      ? empty(t("familyBuilder.notRevit"), [t("familyBuilder.noRevitHint")])
      : empty(t("familyBuilder.noRevit"), [t("familyBuilder.noRevitDesc")]);
  } else if (openId) {
    body = (
      <FamilyBuilderEditor
        key={`${sessionId}:${openId}`}
        artifactId={openId}
        token={token}
        orgId={orgId}
        targetSessionId={sessionId}
        projectId={projectId}
        userId={userId}
        onBack={() => setOpenId(null)}
        onBusyChange={onBusyChange}
      />
    );
  } else {
    body = (
      <FamilyBuilderList
        key={sessionId}
        client={client}
        head={head}
        onOpen={setOpenId}
      />
    );
  }

  return (
    <section className="family-builder-pane" data-pane="family-builder" data-view={openId && sessionId ? "editor" : "list"}>
      {body}
    </section>
  );
}
